import { createInvitationSchema } from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";

type Role = { id: string; name: string };
type Invitation = {
  id: string;
  email: string;
  roleName: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  expiresAt: string;
  invitedBy: string | null;
};
type Created = {
  id: string;
  delivery: "sent" | "existing_account" | "disabled" | "failed" | null;
  acceptUrl: string | null;
};

const STATUS_LABEL = {
  pending: "Pendente",
  accepted: "Aceito",
  revoked: "Revogado",
  expired: "Expirado",
} as const;
const DELIVERY: Record<string, string> = {
  sent: "Convite enviado por e-mail.",
  existing_account:
    "Esta pessoa já tem conta: envie o link abaixo (ela entra com a conta que já tem).",
  disabled: "Envio de e-mail não configurado: copie o link abaixo e envie à pessoa.",
  failed: "Não foi possível enviar o e-mail: copie o link abaixo e envie à pessoa.",
};

export function InvitationsPage() {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ["convites"],
    queryFn: () => apiFetch<Invitation[]>("/convites"),
  });
  const roles = useQuery({ queryKey: ["papeis"], queryFn: () => apiFetch<Role[]>("/papeis") });
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [key, setKey] = useState(newIdempotencyKey);
  const [formError, setFormError] = useState<string | null>(null);
  const [toRevoke, setToRevoke] = useState<Invitation | null>(null);

  const create = useMutation({
    mutationFn: (body: { email: string; roleId: string }) =>
      apiFetch<Created>("/convites", { method: "POST", body, idempotencyKey: key }),
    onSuccess: async () => {
      setEmail("");
      setKey(newIdempotencyKey());
      await queryClient.invalidateQueries({ queryKey: ["convites"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => apiFetch(`/convites/${id}/revogar`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["convites"] }),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = createInvitationSchema.safeParse({ email, roleId });
    if (!parsed.success) return setFormError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setFormError(null);
    create.mutate(parsed.data);
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Convites"
        description="O acesso começa sempre por convite. O link vale por 7 dias e só pode ser usado uma vez."
      />
      <Card title="Novo convite">
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-[1fr_16rem_auto] sm:items-end">
          <Field label="E-mail">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Papel">
            <Select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
              <option value="">Selecione</option>
              {roles.data?.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          </Field>
          <Button type="submit" disabled={create.isPending}>
            Convidar
          </Button>
        </form>
        <div className="mt-4 space-y-3">
          {formError && <Alert tone="error">{formError}</Alert>}
          {create.isError && <Alert tone="error">{errorMessage(create.error)}</Alert>}
          {create.data && (
            <Alert tone="ok">
              {create.data.delivery ? DELIVERY[create.data.delivery] : "Convite criado."}
              {create.data.acceptUrl && (
                <div className="mt-2 flex gap-2">
                  <Input
                    readOnly
                    value={create.data.acceptUrl}
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void navigator.clipboard.writeText(create.data!.acceptUrl!)}
                  >
                    Copiar
                  </Button>
                </div>
              )}
            </Alert>
          )}
        </div>
      </Card>

      <Card title="Convites enviados">
        {list.isPending ? (
          <Spinner />
        ) : list.isError ? (
          <Alert tone="error">{errorMessage(list.error)}</Alert>
        ) : (
          <Table head={["E-mail", "Papel", "Situação", "Validade", ""]}>
            {list.data.map((i) => (
              <tr key={i.id}>
                <td className="px-3 py-2">{i.email}</td>
                <td className="px-3 py-2">{i.roleName}</td>
                <td className="px-3 py-2">
                  <Badge
                    tone={
                      i.status === "accepted" ? "ok" : i.status === "pending" ? "warn" : "neutral"
                    }
                  >
                    {STATUS_LABEL[i.status]}
                  </Badge>
                </td>
                <td className="px-3 py-2">{new Date(i.expiresAt).toLocaleString("pt-BR")}</td>
                <td className="px-3 py-2 text-right">
                  {i.status === "pending" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={revoke.isPending}
                      onClick={() => setToRevoke(i)}
                    >
                      Revogar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
      <ConfirmDialog
        open={toRevoke !== null}
        title="Revogar este convite?"
        confirmLabel="Revogar convite"
        destructive
        busy={revoke.isPending}
        onCancel={() => setToRevoke(null)}
        onConfirm={() => {
          if (toRevoke) revoke.mutate(toRevoke.id);
          setToRevoke(null);
        }}
      >
        O link enviado para {toRevoke?.email ?? "a pessoa"} deixa de funcionar. Para dar acesso
        depois, é preciso enviar um convite novo.
      </ConfirmDialog>
    </div>
  );
}
